// Right alert drawer (PRD §5.8, §6.1): live open incidents by severity, then
// longest-running first, with ACK / Mute / Inspect / Trace.
import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { BellOff, Check, ChevronsLeft, ChevronsRight, Crosshair, Route, VolumeX } from "lucide-react";

import { useListIncidents, useListProbes, type Incident, type Monitor, type MonitorState } from "@/api/generated";
import { useIncidentActions } from "@/components/incidents/actions";
import { SeverityBadge, StatusIcon } from "@/components/status/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { duration } from "@/lib/format";
import { useMonitorMap, useStateMap } from "@/lib/lookups";
import { useNow } from "@/lib/now";
import { SEVERITY } from "@/lib/status";
import { cn } from "@/lib/utils";
import { useUI } from "@/store/ui";

export function sortIncidents(list: Incident[]): Incident[] {
  return [...list].sort((a, b) => {
    const sa = a.suppressed_by ? 1 : 0;
    const sb = b.suppressed_by ? 1 : 0;
    if (sa !== sb) return sa - sb;
    const r = SEVERITY[b.severity].rank - SEVERITY[a.severity].rank;
    if (r !== 0) return r;
    return new Date(a.started_at).getTime() - new Date(b.started_at).getTime();
  });
}

/** Where an ad-hoc trace for this monitor should run from: a probe that sees it failing. */
export function traceFrom(m: Monitor, st?: MonitorState): { target: string; probeId?: string; monitorId: string } {
  const bad = st?.probes.find((p) => p.status === "DOWN" || p.status === "CRITICAL" || p.status === "DEGRADED");
  return { target: m.target_host, probeId: bad?.probe_id ?? st?.probes[0]?.probe_id ?? m.probe_ids[0], monitorId: m.id };
}

function clock(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function AlertItem({
  inc, monitor, state, now, actions,
}: {
  inc: Incident; monitor?: Monitor; state?: MonitorState; now: number; actions: ReturnType<typeof useIncidentActions>;
}) {
  const inspect = useUI((s) => s.inspect);
  const acked = inc.status === "ACKNOWLEDGED";
  return (
    <li className={cn("border-b px-3 py-2.5", (acked || inc.suppressed_by) && "opacity-70")}>
      <div className="flex items-center justify-between gap-2">
        <SeverityBadge severity={inc.severity} />
        <span className="tabular text-xs text-muted-foreground" title={new Date(inc.started_at).toISOString()}>
          {clock(inc.started_at)} · {duration(inc.started_at, null, now)}
        </span>
      </div>
      <Link to="/incidents/$incidentId" params={{ incidentId: inc.id }} className="mt-1 block text-sm font-medium hover:underline">
        {inc.title}
      </Link>
      {(inc.reason || state?.agreement) && (
        <p className="tabular mt-0.5 text-xs text-muted-foreground">
          {inc.reason}
          {state?.agreement && ` (${state.agreement})`}
        </p>
      )}
      {inc.suppressed_by && <p className="mt-0.5 text-xs text-status-maintenance">{inc.bgp_prefix_id ? "Grouped under its ASN's incident" : "Suppressed: a dependency is down"}</p>}
      {acked && <p className="mt-0.5 text-xs text-muted-foreground">Acknowledged</p>}
      {state?.muted && <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><VolumeX className="size-3" /> Muted</p>}
      <div className="mt-1.5 flex flex-wrap gap-1">
        {inc.status === "OPEN" && (
          <Button size="xs" variant="secondary" onClick={() => actions.ack(inc)}><Check /> ACK</Button>
        )}
        {monitor && (
          <>
            <Button size="xs" variant="ghost" onClick={() => actions.mute({ id: monitor.id, name: monitor.name })}><BellOff /> Mute</Button>
            <Button size="xs" variant="ghost" onClick={() => inspect(monitor.id)}><Crosshair /> Inspect</Button>
            <Button size="xs" variant="ghost" onClick={() => actions.trace(traceFrom(monitor, state))}><Route /> Trace</Button>
          </>
        )}
      </div>
    </li>
  );
}

export function AlertDrawer() {
  const open = useUI((s) => s.drawerOpen);
  const toggle = useUI((s) => s.toggleDrawer);
  const incidents = useListIncidents();
  const probes = useListProbes();
  const monitors = useMonitorMap();
  const states = useStateMap();
  const now = useNow();
  const actions = useIncidentActions(probes.data ?? []);
  const list = useMemo(() => sortIncidents(incidents.data ?? []), [incidents.data]);
  const active = list.filter((i) => !i.suppressed_by);
  const counts = { DOWN: 0, CRITICAL: 0, WARNING: 0 };
  for (const i of active) counts[i.severity]++;

  if (!open) {
    return (
      <aside className="hidden w-[60px] shrink-0 flex-col items-center gap-3 border-l bg-sidebar py-3 lg:flex">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={toggle} aria-label="Open alert drawer"><ChevronsLeft /></Button>
          </TooltipTrigger>
          <TooltipContent side="left">Open alerts</TooltipContent>
        </Tooltip>
        {(["DOWN", "CRITICAL", "WARNING"] as const).map((s) => (
          <div key={s} className="flex flex-col items-center gap-0.5" title={`${counts[s]} ${SEVERITY[s].label}`}>
            <StatusIcon status={SEVERITY[s].status} className={cn("size-5", counts[s] === 0 && "opacity-30")} />
            <span className="tabular text-xs">{counts[s]}</span>
          </div>
        ))}
      </aside>
    );
  }

  return (
    <aside className="hidden w-80 shrink-0 flex-col border-l bg-sidebar lg:flex">
      <div className="flex h-12 items-center justify-between border-b px-3">
        <span className="text-sm font-semibold">
          <span className="tabular">{active.length}</span> active alert{active.length === 1 ? "" : "s"}
        </span>
        <Button variant="ghost" size="icon-sm" onClick={toggle} aria-label="Collapse alert drawer"><ChevronsRight /></Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center text-sm text-muted-foreground">
            <StatusIcon status="UP" className="size-6" />
            No open incidents.
          </div>
        ) : (
          <ul>
            {list.map((inc) => {
              const m = inc.monitor_id ? monitors.get(inc.monitor_id) : undefined;
              return <AlertItem key={inc.id} inc={inc} monitor={m} state={m ? states.get(m.id) : undefined} now={now} actions={actions} />;
            })}
          </ul>
        )}
      </div>
      {actions.dialogs}
    </aside>
  );
}
